import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { uploadDocument } from 'api/operations/documentsService';
import { uploadFiles, sanitizeFileName } from 'global/utils/uploadFiles';
import { action, autorun, computed, extendObservable, makeObservable, observable, remove, runInAction } from 'mobx';
import { DocUploadStrategy } from 'types/documentsService';
import { OTHER_DOC_TYPE_OPTION } from './constants';

interface ContextualProps {
  /** Currently uploaded documents' types */
  uploadedDocumentsTypes: string[];
  onSubmit: (entityId: string) => Promise<void>;
  setPageIsLoading: (status: boolean) => void;
  entityId: string;
  enrollmentSponsorId: string;
  groupName: string;
}

const OTHER_OPTION: OptionType = Object.freeze({
  value: OTHER_DOC_TYPE_OPTION,
  label: 'Other',
});

interface UploadDocFields extends DefaultFormFields {
  docType: FormControl<string>;
  [key: string]: FormControl<string>;
}

export class UploadDocumentModalStore {
  private readonly _entityId: string;
  private readonly _enrollmentSponsorId: string;
  private readonly _groupName: string;

  private _uploadedDocumentsTypes: string[] = observable.array<string>([]);
  private _uploadedFile: AcceptedFiles | null = null;
  private _isLoading = false;
  // Controls the loader for the whole page
  private readonly _setPageIsLoading;
  // On submission callback function
  private readonly _onSubmit;

  public form = new FormController<UploadDocFields>({
    docType: new FormControl('', { optional: false }),
  });

  get availableDocOptions(): OptionType[] {
    return [
      ...this._uploadedDocumentsTypes.map((documentType) => ({
        label: documentType,
        value: documentType,
      })),
      OTHER_OPTION,
    ];
  }

  get isDataValid(): boolean {
    return (
      this.form.basicFieldsValidationsPassed &&
      !(this._uploadedFile === null || Object.keys(this._uploadedFile).length < 1)
    );
  }

  get currentUploadStrategy(): DocUploadStrategy {
    return this.form.fields.docType.value === OTHER_OPTION.value ? 'CUSTOM_DOC' : 'PREDEFINED_DOC';
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  private _isTypeDifferentFromTheUploaded(value: string): boolean {
    return !this._uploadedDocumentsTypes.find((docType) => docType.toLowerCase() === value.toLowerCase().trim());
  }

  public setFile(files: AcceptedFiles): void {
    this._uploadedFile = files;
  }

  public resetModal(): void {
    this.form.resetForm();
    this._uploadedFile = null;
  }

  public async requestUploadDocument(): Promise<void> {
    if (!this._uploadedFile) {
      throw new Error('No file uploaded.');
    }
    this._setPageIsLoading(true);

    const originalFileName = Object.keys(this._uploadedFile)[0];

    // Extract file extension from original filename
    const fileExtension = originalFileName.includes('.')
      ? originalFileName.substring(originalFileName.lastIndexOf('.'))
      : '';

    // Create filename and sanitize the entire string
    const convertedFileName = sanitizeFileName(
      `${this._groupName}_${
        this.form.fields.docType.value === OTHER_DOC_TYPE_OPTION
          ? this.form.fields.docName.value
          : this.form.fields.docType.value
      }_${Date.now()}${fileExtension}`,
    );

    // Get upload url
    const uploadUrl = await uploadDocument(
      this._entityId,
      this._enrollmentSponsorId,
      this.currentUploadStrategy === 'CUSTOM_DOC' ? this.form.fields.docName.value : this.form.fields.docType.value,
      convertedFileName,
    );

    await runInAction(async () => {
      if (!this._uploadedFile) {
        throw new Error('No file uploaded.');
      }

      // Create new AcceptedFiles object with renamed key
      const renamedFile: AcceptedFiles = {
        [convertedFileName]: this._uploadedFile[originalFileName],
      };

      // Upload file to bucket
      await uploadFiles(renamedFile, { [convertedFileName]: uploadUrl });
      await runInAction(async () => {
        await this._onSubmit(this._entityId);
      });
    });
    this.resetModal();
    this._setPageIsLoading(false);
  }

  constructor({
    uploadedDocumentsTypes,
    onSubmit,
    setPageIsLoading,
    entityId,
    enrollmentSponsorId,
    groupName,
  }: ContextualProps) {
    this._uploadedDocumentsTypes = uploadedDocumentsTypes;
    this._entityId = entityId;
    this._enrollmentSponsorId = enrollmentSponsorId;
    this._groupName = groupName;
    this._onSubmit = onSubmit;
    this._setPageIsLoading = setPageIsLoading;
    makeObservable<
      UploadDocumentModalStore,
      | '_enrollmentSponsorId'
      | '_uploadedDocumentsTypes'
      | '_uploadedFile'
      | '_isLoading'
      | '_onSubmit'
      | '_entityId'
      | '_groupName'
      | '_isTypeDifferentFromTheUploaded'
    >(this, {
      _enrollmentSponsorId: observable,
      _uploadedDocumentsTypes: observable,
      _uploadedFile: observable,
      _isLoading: observable,
      _entityId: observable,
      _groupName: observable,
      form: observable,

      availableDocOptions: computed,
      isDataValid: computed,
      currentUploadStrategy: computed,
      isLoading: computed,

      _onSubmit: action,
      _isTypeDifferentFromTheUploaded: action.bound,
      requestUploadDocument: action.bound,
      setFile: action.bound,
      resetModal: action.bound,
    });

    autorun(() => {
      if (this.currentUploadStrategy === 'CUSTOM_DOC' && !this.form.fields.docName) {
        extendObservable(this.form.fields, {
          docName: new FormControl('', {
            validations: ['printableCharacters'],
            customValidations: [
              {
                check: this._isTypeDifferentFromTheUploaded.bind(this),
                msg: 'This document is already in the list.',
              },
            ],

            optional: false,
          }),
        });
      }
      if (this.currentUploadStrategy === 'PREDEFINED_DOC') {
        remove(this.form.fields, 'docName');
      }
    });
  }
}
